import _ from 'lodash';

import * as apiLayer from './api-layer';
import { SyncEvent, VoidSyncEvent } from 'ts-events';
import {
  elementAfter,
  SocketType,
  isCameraFrame,
  caseInsensitiveMatch,
} from './util';
import { unpackFrameDataB, unpackFrameDataC } from './util/crypto';
import { WorkerPool } from './util/worker';
import { appActions } from './action';
import { loginEvents, disconnectEvents, dialogEvents } from './component';
import jDataView from 'jdataview';
import jBinary from 'jbinary';
import { netStringTypeset } from './util/net-string';
import { RTCStream } from './rtc';
import { writeToLocalStorage, getSettingByKey } from './util/configHelper';
// import { pipe, interval, map } from 'callbag-basics';
// import * as observe from 'callbag-observe';
// import * as withPrevious from 'callbag-with-previous';
import { logger } from './api/logger';

let SocketWorker = require('worker-loader?name=socket.worker.js!./socket-worker');

interface Callback {
  (message: any, connection: Connection): any;
}

interface Predicate {
  (message: any, connection: Connection): boolean;
}

interface KeyPredicate {
  (key: string, message: any, connection: Connection): boolean;
}

export let screenShareStarted = false;

export let webRtcDisabled = getSettingByKey('web-rtc-disabled');

export const changeWebRtcDisabled = (disabled: boolean) => {
  webRtcDisabled = disabled;
  writeToLocalStorage('web-rtc-disabled', disabled);
};

export abstract class Connection {
  keyCounter = 0;
  useQueue: boolean = true;
  requestQueue = [];
  // socket: WebSocket
  socketPool: WorkerPool;
  reconnect: boolean = true;
  reconnectInterval = undefined;
  callbacks: { [key: string]: Function } = {};
  listeners: {
    predicate: Predicate;
    callback: Callback;
    once?: boolean;
  }[] = [];
  bufferListeners: Callback[] = [];
  keyedListeners: {
    predicate: KeyPredicate;
    callbacks: {
      [key: string]: Callback;
    };
  }[] = [];
  fallbackListeners: Callback[] = [];
  logPackets: boolean = false;
  host: string;
  port: string;
  connected: boolean;
  encrypted: boolean;
  ofb: boolean = false;
  key: string = undefined;
  iv: string = undefined;
  socketType: SocketType;
  disconnecting: boolean = false;
  connectResolve: (value?: WebSocket | Thenable<WebSocket>) => void;
  path: string = '';
  traversalConnection: TraversalConnection;

  constructor(public poolSize = 3, public isDefault: boolean = false) {}

  useWorker = () => {
    return this.poolSize !== 0;
  };

  abstract onMessage = (message?: any) => {};

  connect = (serverId: string, shopApiKey: string) => {
    const traversalServerUrl =
      process.env.TRAVERSAL_SERVER ||
      this.inferTraversalServerUrl(window.location);
    let connectPromise = this.traversalConnection.connect(
      traversalServerUrl,
      serverId,
      shopApiKey,
    );
    this.ofb = false;
    return connectPromise.then(() => {
      this.connected = true;
    });
  };

  inferTraversalServerUrl = (location: Location) => {
    const proto = location.protocol.replace(/^http(s)?:/, 'ws$1:'); // http[s]: -> ws[s]:
    const host = location.host.replace(/^([^.]+)\./, 'traversal.'); // subdomain.[domain[:port]] -> traversal.[domain[:port]]
    return `${proto}//${host}/Route`;
  };

  setTraversalConnection = (c: TraversalConnection) => {
    this.traversalConnection = c;
    this.traversalConnection.events.message.attach(this.onDeserialize);
    this.connected = this.traversalConnection.hasServer;
    this.traversalConnection.events.gotServer.attach(() => {
      this.connected = true;
    });
    this.traversalConnection.events.connect.attach(this.handleConnect);
    this.traversalConnection.events.disconnect.attach(this.disconnect);
    return this;
  };

  private handleConnect = () => {
    disconnectEvents.reconnect();
  };

  disconnect = (normally: boolean = true) => {
    if (!this.connected) {
      return;
    }

    this.disconnecting = true;

    // if (this.socket) {
    //     this.socket.onclose = () => {};
    //     this.socket.close(1000);
    //     this.socket = undefined
    // }

    if (!normally) {
      apiLayer.disconnectedFromUlterius();
      disconnectEvents.disconnect();
    }

    if (this.useWorker() && this.socketPool) {
      this.socketPool.terminate();
    }
    this.connected = false;
    if (this.isDefault) appActions.setHost({ host: '', port: '' });
  };

  abstract send = (action: string, ...rest: any[]) => {};

  promiseToSendPacket = (packet, packetName: string = 'unnamed') => {
    //LOG console.log("Promising to send packet...", packet)
    if (packetName === 'startscreenshare') {
      screenShareStarted = true;
    } else if (packetName === 'stopscreenshare') {
      screenShareStarted = false;
    }

    return this.sendPacket(packet, packetName);
  };

  sendPacket = (packet, packetName: string = 'unnamed') => {
    if (!this.connected) {
      return;
      // return Promise.reject(Error('Trying to send packet while disconnected'));
    }
    let { logPackets, requestQueue, socketPool } = this;
    if (logPackets) {
      console.groupEnd();
      console.groupCollapsed('Sending packet: ' + packetName);
      //console.log(packet)
      console.groupEnd();
    }

    return this.traversalConnection.sendRawToServer(packet);
  };

  encrypt = (key: string, iv: string) => {
    this.key = key;
    this.iv = iv;
    this.encrypted = true;
  };

  unencrypt = () => {
    this.encrypted = false;
    this.key = undefined;
    this.iv = undefined;
  };

  // setWorkerEvents = () => {
  //     this.socketPool.listen({
  //         deserialize: (message) => {
  //             if (typeof message === "string") {
  //                 this.onArrayBuffer(message)
  //             }
  //             else {
  //                 this.onDeserialize(message)
  //             }
  //         },
  //         serialize: (message) => {
  //             try {
  //                 this.socket.send(message)
  //             }
  //             catch (e) {
  //                 console.log(e)
  //             }
  //         },
  //         decryptBuffer: (message) => {
  //             this.onArrayBuffer(message)
  //         }
  //     })
  // }

  onString = () => {};

  //base64 string, by the way
  // onArrayBuffer = (data: string) => {
  //     this.requestQueue = []
  //     this.bufferListeners.forEach(listener => {
  //         listener(data, this)
  //     })
  // }

  private onDeserialize = (message: any) => {
    this.onMessage(message);
    let caught = false;
    for (let listener of this.listeners) {
      if (listener.predicate(message, this)) {
        let removeListener = listener.callback(
          this.messageFilter(message),
          this,
        );
        caught = true;
        if (listener.once || removeListener === true) {
          _.pull(this.listeners, listener);
        }
      }
    }
    for (let listener of this.keyedListeners) {
      _.forOwn(listener.callbacks, (callback, k) => {
        if (listener.predicate(k, message, this)) {
          caught = true;
          callback(this.messageFilter(message), this);
        }
      });
    }
    if (!caught) {
      for (let listener of this.fallbackListeners) {
        listener(this.messageFilter(message), this);
      }
    }
  };

  // setSocketEvents = () => {
  //     let {
  //         socket,
  //         socketPool,
  //         host,
  //         port,
  //         isDefault,
  //         logPackets,
  //         callbacks
  //     } = this
  //     try {
  //         if (this.reconnectInterval === undefined) {
  //             socket.onerror = (e) => {
  //                 this.socket.close(1000)
  //                 if (this.useWorker) this.socketPool.terminate()
  //                 if (this.isDefault && appStore.getState().connection.host) {
  //                     this.reconnectInterval = setInterval(() => {
  //                         console.log("Not connected... trying to reconnect.")
  //                         //this.connect(host, port)
  //                     }, 4000)
  //                 }
  //                 else {
  //                     loginEvents.fail("Failed to connect, host invalid.")
  //                     messageActions.message({ style: "danger", text: "Failed to connect. Host invalid." })
  //                 }
  //             }
  //         }
  //         socket.onopen = () => {
  //             console.log(`Socket (${host}:${port}${isDefault ? ", Default" : ""}) Status: ${socket.readyState} (open)`)
  //             if (this.reconnectInterval !== undefined && socket.readyState === 1) {
  //                 clearInterval(this.reconnectInterval)
  //                 disconnectEvents.reconnect()
  //                 this.reconnectInterval = undefined
  //             }
  //             if (socket.readyState === 1 && isDefault) {
  //                 appActions.setHost({host, port})
  //             }
  //             this.connectResolve(socket)
  //         }
  //         socket.onmessage = (e) => {
  //             const {key, iv, ofb} = this
  //             if (typeof e.data === "string" || e.data instanceof ArrayBuffer) {
  //                 this.deserialize(e.data)
  //             }
  //             else if (e.data instanceof Blob) {
  //                 console.log("Blob get " + e.data)
  //             }
  //         }
  //         socket.onclose = (e) => {
  //             console.log("Closed socket event: ", e.code)
  //             if (
  //                 !this.disconnecting &&
  //                 e.code !== 1000 &&
  //                 this.reconnect &&
  //                 this.reconnectInterval === undefined &&
  //                 appStore.getState().connection.host
  //             ) {
  //                 console.log("Socket... died? Trying to reconnect in a sec...")
  //                 if (isDefault) {
  //                     apiLayer.disconnectedFromUlterius()
  //                     disconnectEvents.disconnect()
  //                 }
  //                 this.reconnectInterval = setInterval(() => {
  //                     console.log("Disconnected. Trying to reconnect now...")
  //                     //this.connect(this.host, this.port)
  //                 }, 4000)
  //             } else {
  //               console.log("Not attempting reconnect. ", !this.disconnecting,
  //                           e.code !== 1000,
  //                           this.reconnect,
  //                           this.reconnectInterval === undefined,
  //                           appStore.getState().connection.host)
  //             }
  //             this.connected = false
  //             this.socketPool.terminate()
  //         }
  //     }
  //     catch (e) {
  //         console.log(e)
  //     }
  // }

  // deserialize = (data: string | ArrayBuffer) => {
  //     const {key, iv, ofb, socketType} = this
  //     if (this.useWorker()) {
  //         if (data instanceof ArrayBuffer) {
  //             this.socketPool.post("deserialize", {
  //                 key, iv, ofb, data: data, type: socketType
  //             }, [data])
  //         }
  //         else if (typeof data === "string") {
  //             this.socketPool.post("deserialize", {
  //                 key, iv, ofb, data: data, type: socketType
  //             })
  //         }
  //     }
  //     else {
  //         let dataAny = data as any
  //         let ret
  //         if (!(key && iv)) {
  //             ret = JSON.parse(dataAny)
  //         }
  //         try {
  //             ret = JSON.parse(dataAny)
  //         }
  //         catch (err) {
  //             ret = decrypt(key, iv, dataAny, socketType, ofb)
  //         }
  //         if (typeof ret === "string") {
  //             this.onArrayBuffer(ret)
  //         }
  //         else {
  //             this.onDeserialize(ret)
  //         }
  //     }
  // }

  messageFilter = message => {
    return message;
  };

  listen = (predicate: Predicate, callback: Callback) => {
    this.listeners.push({
      predicate,
      callback,
      once: false,
    });
  };

  listenAll = <T extends Connection>(
    ...listeners: [
      (message: any, connection: T) => boolean,
      (message: any, connection: T) => any
    ][]
  ) => {
    for (let listener of listeners) {
      this.listeners.push({
        predicate: listener[0],
        callback: listener[1],
        once: false,
      });
    }
  };
  listenKeys = <T extends Connection>(
    predicate: (key: string, message: any, connection: T) => boolean,
    callbacks: { [key: string]: (message: any, connection: T) => any },
  ) => {
    this.keyedListeners.push({
      predicate,
      callbacks,
    });
  };
  listenBuffer = (callback: Callback) => {
    this.bufferListeners.push(callback);
  };
  listenOnce = (predicate: Predicate, callback: Callback) => {
    this.listeners.push({
      predicate,
      callback,
      once: true,
    });
  };
  fallbackListen = (callback: Callback) => {
    this.fallbackListeners.push(callback);
  };

  //override this for queue usage
  doesMessageMatch = (message, queueMessage) => {
    return true;
  };
}

//keep this function in the module, since it relies heavily on the api import
function defaultHandleMessage(message: ApiMessage) {
  let caught = false;
  for (let endpoint of Object.keys(apiLayer)) {
    /*
        let handler = apiLayer[_.find(Object.keys(apiLayer), fn => {
            return fn == endpoint
        })]
        */
    if (
      message.endpoint &&
      message.endpoint.toLowerCase() == endpoint.toLowerCase() &&
      typeof apiLayer[endpoint] == 'function'
    ) {
      apiLayer[endpoint](message.results);
      caught = true;
    }
  }
  if (message.endpoint && apiLayer.listeners[message.endpoint.toLowerCase()]) {
    for (let fn of apiLayer.listeners[message.endpoint.toLowerCase()]) {
      fn(message.results);
    }
  }
  /*
    if (!caught) {
        console.log("Uncaught message: " + message.endpoint)
        console.log(message)
    }
    */
}

function endpointSendFn(
  getSyncKey: () => string,
  sendPacket: (packet: any, endpoint: string) => any,
) {
  return function(action: string, args?) {
    let packet: any = {
      endpoint: action.toLowerCase(),
      synckey: getSyncKey(),
    };
    if (typeof args !== 'undefined') {
      if (args instanceof Array) {
        packet.args = args;
      } else {
        packet.args = [args];
      }
    }
    return sendPacket(packet, packet.endpoint);
  };
}

export class UlteriusConnection extends Connection {
  correlationId: number = 1;
  socketType = SocketType.Main;
  bindLegacy: boolean = true;
  asyncResolves: {
    [syncKey: string]: (value?: any | Thenable<any>) => void;
  } = {};
  send = (action, args?) => {
    //LOG console.log("Main connection sending:",action, args)
    var packet: any = {
      endpoint: action.toLowerCase(),
      synckey: this.getSyncKey('normal'),
    };
    if (typeof args !== 'undefined') {
      if (args instanceof Array) {
        packet.args = args;
      } else {
        packet.args = [args];
      }
    }
    return this.promiseToSendPacket(packet, packet.endpoint);
  };

  sendAsync = (action: string, ...rest) => {
    let { callbacks } = this;
    let key = this.getSyncKey('override');
    let packet: any = {
      endpoint: action,
      synckey: key,
    };
    let args = [];
    let callback = undefined;
    for (let arg of rest) {
      if (typeof arg !== 'function') {
        args.push(arg);
      }
    }
    args = _.flattenDeep(args);
    if (args.length > 0) {
      packet.args = args;
    }
    if (typeof rest[rest.length - 1] === 'function') {
      callbacks[key] = rest[rest.length - 1];
    } else {
      //callbacks[key] = console.log.bind(console)
    }

    return new Promise<any>((resolve, reject) => {
      this.promiseToSendPacket(packet, packet.endpoint).then(() => {
        _.assign(this.asyncResolves, { [key]: resolve });
        setTimeout(() => {
          reject('Timed out.');
          delete this.asyncResolves[key];
        }, 10 * 1000);
      });
    });
  };

  sendAsyncBatch = (
    type: string,
    restOfPacket: any,
    callback: (message: any) => any,
  ): number => {
    //console.log("sendAsyncBatch")
    let correlationId = this.nextCorrelationId();
    this.send(type, _.assign({}, restOfPacket, { correlationId }));
    this.listen(
      msg => msg.correlationId && msg.correlationId == correlationId,
      callback,
    );

    return correlationId;
  };

  doesMessageMatch = (message, queueMessage) => {
    return message.synckey == queueMessage.synckey;
  };

  onMessage = (message: ApiMessage) => {
    let {
      // socket,
      socketPool,
      host,
      port,
      isDefault,
      logPackets,
      callbacks,
    } = this;

    let { synckey, results, endpoint } = message;
    /*
        if (endpoint && endpoint.toLowerCase() == "connectedtoulterius") {
            this.requestQueue = []
            //flush it all
        }
        */
    let packet = _.find(
      this.requestQueue,
      (m: any) =>
        m.synckey == synckey ||
        (endpoint &&
          endpoint.toLowerCase() == 'aeshandshake' &&
          m.endpoint.toLowerCase() == 'aeshandshake'),
    );

    if (endpoint && endpoint != 'getcameraframe' && !isCameraFrame(message)) {
      if (logPackets) {
        console.groupCollapsed('Got packet: ' + endpoint);
        //console.log(message)
        if (packet) {
          console.log('matching request found');
          console.log(
            'pending requests left: ' +
              (this.requestQueue.length ? this.requestQueue.length - 1 : 0),
          );
          console.log(this.requestQueue);
        }
        console.groupEnd();
      }
    }

    if (packet) {
      if (elementAfter(this.requestQueue, packet)) {
        this.sendPacket(elementAfter(this.requestQueue, packet));
      }
      _.pull(this.requestQueue, packet);
    }

    if (
      endpoint &&
      (!synckey || (synckey as string).indexOf('override') == -1) &&
      this.bindLegacy
    ) {
      defaultHandleMessage(message);
    } else {
      if (callbacks[synckey] && typeof callbacks[synckey] == 'function') {
        callbacks[synckey](results, message);
        delete callbacks[synckey];
      }
      if (this.asyncResolves[synckey]) {
        this.asyncResolves[synckey](results);
        delete this.asyncResolves[synckey];
      }
    }
  };

  getSyncKey = (prepend: string) => {
    this.keyCounter++;
    return prepend + String(this.keyCounter);
  };

  messageFilter = message => {
    return message.results;
  };

  nextCorrelationId = () => {
    return this.correlationId++;
  };
}

class TerminalConnection extends Connection {
  correlationId: number = 1;
  socketType = SocketType.Terminal;

  private currentSyncKey = 0;

  getSyncKey = () => {
    return String(this.currentSyncKey++);
  };

  send = (type: string, restOfPacket: any) => {
    //this.socket.send(JSON.stringify(packet))
    let packet = _.assign({}, { type }, restOfPacket);
    this.promiseToSendPacket(packet, 'Terminal');
  };

  callEndpoint = endpointSendFn(this.getSyncKey, (packet, endpoint) => {
    this.promiseToSendPacket(packet, endpoint);
  });

  sendAsync = (
    type: string,
    restOfPacket: any,
    callback: (message: any) => any,
  ) => {
    let correlationId = this.nextCorrelationId();
    this.send(type, _.assign({}, restOfPacket, { correlationId }));
    this.listenOnce(
      msg => msg.correlationId && msg.correlationId == correlationId,
      callback,
    );
  };
  onMessage = (message: any) => {
    this.requestQueue = [];
  };
  nextCorrelationId = () => {
    return this.correlationId++;
  };
  messageFilter = message => {
    return message.results;
  };
}

class ScreenShareConnection extends Connection {
  loggedIn: boolean = true;
  socketType = SocketType.ScreenShare;
  failures: number = 0;
  send = (packet: any) => {
    this.promiseToSendPacket(packet, 'ScreenShare');
  };
  callEndpoint = endpointSendFn(
    () => '1',
    (packet, endpoint) => {
      this.promiseToSendPacket(packet, endpoint);
    },
  );
  sendMessage = (action: string, args?) => {
    var packet: any = {
      endpoint: action.toLowerCase(),
    };
    if (typeof args !== 'undefined') {
      if (args instanceof Array) {
        packet.args = args;
      } else {
        packet.args = [args];
      }
    }
    this.promiseToSendPacket(packet, packet.endpoint);
  };
  sendEvent = (EventType: string, Action: string, additional: any = {}) => {
    if (!this.loggedIn) return;

    let packet = {
      EventType,
      Action,
    };
    _.assign(packet, additional);
    this.promiseToSendPacket(packet, packet.Action);
  };
  onConnect = () => {
    this.loggedIn = true;
  };
  onMessage = (message: any) => {
    this.requestQueue = [];
    //console.log(message)
    if (message.endpoint == 'login') {
      //console.log(message)
    }
    if (message.endpoint == 'login' && message.results.loggedIn) {
      this.loggedIn = true;
    }
  };
  messageFilter = message => {
    return message.results;
  };
}

export class TraversalConnection {
  reconnectTimeout: any;
  private reconnectAttempts: number = 0;
  private url: string;
  socket: WebSocket | null;
  hasServer = false;
  serverId: string = '';
  shopApiKey: string = '';
  ownId: string = '';
  credentials: { username: string; password: string };
  resolves: { [syncKey: string]: (arg: any) => any } = {};
  haveConnection: Promise<any>;
  private haveConnectionResolve: () => any;
  haveServer: Promise<any>;
  heartbeatCount = 0;
  heartbeatInterval;
  private haveServerResolve: () => any;
  private currentSyncKey = 0;
  events = {
    connect: new VoidSyncEvent(),
    gotServer: new VoidSyncEvent(),
    message: new SyncEvent<ApiMessage>(),
    rawMessage: new SyncEvent<any>(),
    disconnect: new SyncEvent<boolean>(),
  };
  trySendMessagesInterval: NodeJS.Timer | undefined | null;

  constructor() {
    this.haveServer = new Promise((resolve, reject) => {
      this.haveServerResolve = resolve;
    });
    this.haveConnection = new Promise(resolve => {
      this.haveConnectionResolve = resolve;
    });
  }

  getSyncKey = () => {
    return String(this.currentSyncKey++);
  };

  setServer = (id: string) => {
    this.hasServer = !!id;
    this.serverId = id;
  };

  clearServer = () => {
    this.hasServer = false;
    this.serverId = '';
  };

  packMessage = (
    target: 'Server' | 'Pass',
    packet: any,
    recipient?: string,
  ): Uint8Array => {
    //console.log(packet)
    let packetData, packetLength;

    if (target === 'Pass' && !recipient) {
      // if it has come null/empty after all, let's send it to keep the Pass packet well-formed
      recipient = '--';
    }

    if (packet instanceof ArrayBuffer) {
      packetData = new Int8Array(packet);
      packetLength = packetData.byteLength;
    } else if (typeof packet === 'string') {
      packetData = packet;
      packetLength = packetData.length + 4;
    } else {
      packetData = JSON.stringify(packet);
      packetLength = packetData.length + 4;
    }

    let bufferLength =
      packetLength +
      target.length +
      4 +
      (recipient ? recipient.length + 4 : 0) +
      (target === 'Pass' ? 1 : 0);

    if (typeof TextEncoder == 'undefined') {
      let v = new jBinary(
        new jDataView(bufferLength, 0, bufferLength, true),
        netStringTypeset,
      );
      v.write('NET_String', target);
      recipient && v.write('NET_String', recipient);
      if (target === 'Pass') {
        if (packet instanceof ArrayBuffer) {
          v.write('byte', 0x00); // binary
        } else if (typeof packet === 'string') {
          v.write('byte', 0x01); // raw string
        } else {
          v.write('byte', 0x02); // JSON
        }
      }

      if (typeof packetData === 'string') {
        v.write('NET_String', packetData);
      } else {
        v.write(['array', 'byte'], packetData);
      }

      let bufferArray = new Uint8Array(v.view.buffer);
      return bufferArray.subarray(0, v.view.tell());
    } else {
      var offset = 0;
      var encoder = new TextEncoder();
      var array = new Uint8Array(bufferLength);

      offset = this.writeStringIntoArray(target, array, offset, encoder);

      if (recipient) {
        offset = this.writeStringIntoArray(recipient, array, offset, encoder);
      }

      if (target === 'Pass') {
        if (packet instanceof ArrayBuffer) {
          array.set([0x00], offset); // binary
        } else if (typeof packet === 'string') {
          array.set([0x01], offset); // string
        } else {
          array.set([0x02], offset); // json
        }
        offset += 1;
      }

      if (packetData instanceof Int8Array) {
        array.set(packetData, offset);
        offset += packet.byteLength;
      } else if (typeof packetData == 'string') {
        offset = this.writeStringIntoArray(packetData, array, offset, encoder);
      } else {
        // This shouldn't ever happen.
        console.warn('Unknown packet type.');
        return;
      }

      return array.subarray(0, offset);
    }
  };

  writeStringIntoArray = (
    value: string,
    array: Uint8Array,
    currentOffset: number,
    encoder: TextEncoder,
  ): number => {
    var stringLength = value.length;
    while (stringLength >= 0x80) {
      array.set([stringLength | 0x80], currentOffset);
      currentOffset += 1;
      stringLength >>= 7;
    }
    array.set([stringLength], currentOffset);
    currentOffset += 1;

    var result = encoder.encode(value);
    array.set(result, currentOffset);
    currentOffset += result.byteLength;
    return currentOffset;
  };

  unpackMessage = (buffer: Uint8Array) => {
    let v = new jBinary(
      new jDataView(buffer, 0, buffer.length, true),
      netStringTypeset,
    );
    let senderId = v.read('NET_String');
    let endpoint = v.read('NET_String');
    const endpointMatches = epStr => caseInsensitiveMatch(endpoint, epStr);
    let body;
    if (endpointMatches('screensharedata')) {
      body = {
        endpoint: 'screensharedata',
        results: unpackFrameDataB(v),
      };
    } else if (endpointMatches('screensharedatac')) {
      body = {
        endpoint: 'screensharedata',
        results: unpackFrameDataC(v),
      };
    } else {
      let header = JSON.parse(v.read('NET_String'));
      // Added "or" check for synckey to allow null results to
      // be returned.  Otherwise, it would think this is a binary
      // message.
      if (header.results || header.synckey) {
        body = header;
      } else {
        body = {
          endpoint: endpoint,
          results: header,
        };

        let binary = new Uint8Array((v as any).view.buffer).subarray(v.tell());
        if (binary.length > 0) {
          _.assign(body.results, { binary });
        }

        if (header.correlationId) {
          let correlationId = header.correlationId;
          _.assign(body, { correlationId });
        }
      }
    }

    return { senderId, endpoint, body };
  };

  authenticate = (username: string, password: string) =>
    this.sendAuthenticateFlow('authenticate', [username, password, 'Master']);

  sendAuthenticateFlow: (
    action: string,
    args?: any[],
  ) => Promise<any> = endpointSendFn(
    this.getSyncKey,
    packet =>
      new Promise(resolve => {
        console.debug('Sending', packet);
        this.sendInternalAuthenticateFlow(
          this.packMessage('Server', packet),
        ).then(() => {
          this.resolves[packet.synckey] = resolve;
        });
      }),
  );

  sendToServer: (action: string, args?: any[]) => Promise<any> = endpointSendFn(
    this.getSyncKey,
    packet =>
      new Promise(resolve => {
        //console.log("Sending to server", packet)
        //console.log(this.hasServer)
        //console.log(this.serverId)
        this.sendInternal(this.packMessage('Pass', packet, this.serverId)).then(
          () => {
            this.resolves[packet.synckey] = resolve;
          },
        );
      }),
  );

  sendRawToServer = data =>
    this.hasServer
      ? this.sendInternal(this.packMessage('Pass', data, this.serverId))
      : Promise.resolve(0);

  private sendInternal = (message: Uint8Array): Promise<any> =>
    this.haveServer.then(() => this.socket.send(message));

  private sendInternalAuthenticateFlow = (message: Uint8Array): Promise<any> =>
    this.haveConnection.then(() => this.socket.send(message));

  getCredentials = () => {
    return this.sendAuthenticateFlow('requestcreds');
  };

  connect = (
    url: string,
    serverId: string,
    shopApiKey: string,
    type = 'Master',
  ) => {
    if (this.socket) {
      console.debug('Closing previous socket');
      this.socket.removeEventListener('close', this.handleClose);
      this.socket.removeEventListener('error', this.handleError);
      this.socket.close();
      this.events.disconnect.post(false);

      this.haveServer = new Promise((resolve, reject) => {
        this.haveServerResolve = resolve;
      });
      this.haveConnection = new Promise(resolve => {
        this.haveConnectionResolve = resolve;
      });
    }

    this.hasServer = false;

    this.url = url;
    this.serverId = serverId;
    this.shopApiKey = shopApiKey;

    this.socket = new WebSocket(url);
    this.socket.binaryType = 'arraybuffer';
    const connectPromise = new Promise(resolve => {
      this.socket.addEventListener('open', event => {
        this.handleOpen(event);
        resolve();
      });
    });
    this.socket.addEventListener('message', this.handleMessage);
    this.socket.addEventListener('close', this.handleClose);
    this.socket.addEventListener('error', this.handleError);

    return connectPromise;
  };

  private handleOpen = event => {
    this.events.connect.post();
    this.runHearbeat();
    clearTimeout(this.reconnectTimeout);
    this.haveConnectionResolve();
    console.log('Traversal server connection open.');
    this.getCredentials().then(cr => {
      console.log('Got Credenzas', cr);
    });

    // Disable this logging for now, cause it's pretty damn useless
    // Revisit in 3 weeks, deadline: 25.06.2018

    // pipe(
    //   interval(500),
    //   map(() => this.socket.bufferedAmount),
    //   withPrevious,
    //   observe(
    //     ([bufferedAmount, prevBuffedAmount, firstCall]: [
    //       number,
    //       number,
    //       boolean
    //     ]) => {
    //       const delta = !firstCall ? bufferedAmount - prevBuffedAmount : 0;
    //       const percentDelta =
    //         !!prevBuffedAmount && !firstCall
    //           ? delta * 100 / prevBuffedAmount
    //           : 0;

    //       console.debug(
    //         // prettier-ignore
    //         `WS buffered amount: ${bufferedAmount}, previous: ${prevBuffedAmount}, delta: ${delta}, ${percentDelta}%`,
    //       );
    //     },
    //   ),
    // );
  };

  private runHearbeat = () => {
    this.heartbeatInterval = setInterval(() => {
      if (this.heartbeatCount >= 5) {
        this.tryReconnecting();
      } else {
        this.heartbeatCount++;
        this.sendAuthenticateFlow('heartbeat', ['ping']);
        console.debug(
          'Sent WebSocket heartbeat. Current count: ' + this.heartbeatCount,
        );
      }
    }, 5000);
  };

  private handleMessage = e => {
    this.heartbeatCount = 0;
    let msg;
    try {
      msg = JSON.parse(e.data);
    } catch (er) {
      //console.log(e)
      const unpacked = this.unpackMessage(new Uint8Array(e.data));
      msg =
        typeof unpacked.body === 'string'
          ? JSON.parse(unpacked.body)
          : unpacked.body;
      msg.senderId = unpacked.senderId;
    }
    //console.log(msg)
    let results = msg.results;
    //console.log(this.resolves)
    if (msg.synckey && typeof this.resolves[msg.synckey] === 'function') {
      console.debug(`Message with key: ${msg.synckey} has been resolved`);
      this.resolves[msg.synckey](msg.results);
      delete this.resolves[msg.synckey];
    }
    this.events.rawMessage.post(msg);
    if (msg.endpoint) {
      if (msg.endpoint !== 'authenticate') {
        this.events.message.post(msg);
      }

      if (
        msg.endpoint !== 'authenticate' &&
        msg.endpoint !== 'heartbeat' &&
        msg.endpoint !== 'recipientnotfound'
      ) {
        clearInterval(this.trySendMessagesInterval);
        this.trySendMessagesInterval = null;
      }

      if (msg.endpoint == 'requestcreds' && results.tempCreds.valid) {
        this.authenticate(this.shopApiKey, this.serverId)
          .then(e => {
            console.log('Master authenticated.');
            this.ownId = results.tempCreds.id;
            //this.setServer("1C3 69F 4BF".replace(/\s/g, ""))

            //this.sendToServer("requestprocessinformation")
            return this.sendAuthenticateFlow('doesslaveexist', [
              this.serverId.replace(/\s/g, ''),
            ]);
          })
          .then(response => {
            if (response.exist) {
              this.setServer(this.serverId.replace(/\s/g, ''));
              this.reconnectAttempts = 0;
              this.events.gotServer.post();
              this.haveServerResolve();
              this.events.message.post({
                endpoint: 'authenticate',
                synckey: 'a',
                results: {
                  authenticated: true,
                },
              });
            }
            if (response.exist === false) {
              logger.trackOnetime('remote_host_not_found');
              loginEvents.fail('Remote host not found.');
              console.debug(`Remote host not found, let's try to reconnect`);
              this.tryReconnecting();
            }
            if (response.noAuth === true) {
              loginEvents.fail('Authentication Failed.');
            }
          });
        //this.authenticate(results.tempCreds.username, results.tempCreds.password)
      } else if (msg.endpoint == 'recipientnotfound' && this.hasServer) {
        this.trySendMessages();
      }
    }
  };

  private handleClose = event => {
    console.debug('WS connection closed', event);

    if (event.code !== 1000) {
      this.tryReconnecting();
    }
  };

  private handleError = event => {
    console.debug('WS connection errored', event);
    loginEvents.fail('WS connection failed');
    // this.tryReconnecting();
  };

  private tryReconnecting = () => {
    if (this.reconnectAttempts >= 5) {
      console.debug('Reconnect failed');
      this.miserablyFail('Connection was lost');
      return;
    }

    const timeUntilRecconnectAttempt = 2000 * this.reconnectAttempts;

    console.debug(`Try reconnecting in ${timeUntilRecconnectAttempt} ms`);

    const reconnect = () => {
      console.debug(`Re-connection try number: ${this.reconnectAttempts + 1}`);
      this.connect(this.url, this.serverId, this.shopApiKey);
      this.reconnectAttempts++;
    };

    this.reconnectTimeout = setTimeout(reconnect, timeUntilRecconnectAttempt);
  };

  private trySendMessages(): void {
    if (this.trySendMessagesInterval) {
      console.warn('trySendMessages already started');
      return;
    }
    // todo: don't retry, rather wait from the traversal server for "agent is back online" event.
    console.log(`agent lost`);
    const maxTime = 10 * 60 * 1000; // 10 minutes
    const interval = 10 * 1000; // 10 seconds
    let count = 0;
    this.trySendMessagesInterval = setInterval(() => {
      if (count * interval > maxTime) {
        clearInterval(this.trySendMessagesInterval);
        this.trySendMessagesInterval = null;
        this.miserablyFail('Remote host is gone');
        return;
      }

      if (screenShareStarted) {
        console.log(
          `sending startscreenshare on ${count} count from WebSocket reconnect`,
        );
        screenConnection.callEndpoint('startscreenshare');
      }

      count++;
    }, interval);
  }

  private miserablyFail = (message: string) => {
    clearInterval(this.heartbeatInterval);
    this.clearServer();

    // This will hide the "Attempting to reconnect" overlay.
    disconnectEvents.reconnect();
    dialogEvents.dialog({
      title: 'Connection failed.',
      body: `${message}, click Close to reload the page.`,
      onClose: () => window.location.reload(),
    });
  };
}

export class WSConnectionSingleton {
  private static wsTraversalConnection: TraversalConnection;

  public static getConnection = (): TraversalConnection => {
    if (!WSConnectionSingleton.wsTraversalConnection) {
      WSConnectionSingleton.wsTraversalConnection = new TraversalConnection();
    }

    return WSConnectionSingleton.wsTraversalConnection;
  };
}

export let terminalConnection = new TerminalConnection(
  1,
  false,
).setTraversalConnection(WSConnectionSingleton.getConnection());
_.assign(terminalConnection, {
  path: 'terminal',
  logPackets: true,
  useQueue: false,
});

export let screenConnection = new ScreenShareConnection(
  1,
  false,
).setTraversalConnection(WSConnectionSingleton.getConnection());
_.assign(screenConnection, {
  path: 'screenshare',
  logPackets: false,
  useQueue: false,
  reconnect: false,
});

export let mainConnection = new UlteriusConnection(
  2,
  true,
).setTraversalConnection(WSConnectionSingleton.getConnection());
_.assign(mainConnection, {
  path: 'api',
  logPackets: false,
  useQueue: false,
});

export let alternativeConnection = new UlteriusConnection(2, false);
_.assign(alternativeConnection, {
  path: 'webcam',
  bindLegacy: false,
  useQueue: false,
  logPackets: false,
  reconnect: false,
});

export class RTCStreamSingleton {
  private static wsStream: RTCStream;

  public static getConnection = (): RTCStream => {
    if (webRtcDisabled) {
      return new RTCStream();
    }

    if (!RTCStreamSingleton.wsStream) {
      RTCStreamSingleton.wsStream = new RTCStream().setConnection(
        WSConnectionSingleton.getConnection(),
      );
    }

    return RTCStreamSingleton.wsStream;
  };
}

window.onbeforeunload = () => {
  terminalConnection.disconnect();
  screenConnection.disconnect();
  mainConnection.disconnect();
  alternativeConnection.disconnect();
};

//legacy module api for the main connection
export let sendCommandToDefault = mainConnection.send;

export function sendCommand(sock: WebSocket, action: string, args?: any) {
  sendCommandToDefault(action, args);
}

export let sendCommandAsync = mainConnection.sendAsync;
export let sendCommandAsyncBatch = mainConnection.sendAsyncBatch;

export function getSenderFactory(connection: Connection) {
  return (endpoint: string) => (...args: any[]) => {
    if (args.length) {
      connection.send(endpoint, args);
    } else {
      connection.send(endpoint);
    }
  };
}
